<script lang="ts" setup>
import { BrazeCard } from '~/types/Content'

type BrazeContentCardProps = {
  card: BrazeCard
  variant?: 'my-account' | 'unknown'
}

const props = withDefaults(defineProps<BrazeContentCardProps>(), {
  variant: 'unknown',
})
</script>

<template>
  <div class="content-card" :class="`content-card--${props.variant}`">
    <Anchor v-if="card.url && card.imageUrl" :url="card.url">
      <img class="content-card__image" :src="card.imageUrl" :alt="card.title" />
    </Anchor>
    <img v-else-if="card.imageUrl" class="content-card__image" :src="card.imageUrl" :alt="card.title" />
    <div v-if="card.title || card.description || card.linkText" class="content-card__content">
      <h2 v-if="card.title" class="content-card__title heading-2">{{ card.title }}</h2>
      <p v-if="card.description" class="content-card__description">{{ card.description }}</p>
      <Anchor v-if="card.url && card.linkText" :url="card.url" variant="tertiary" class="content-card__link">{{
        card.linkText
      }}</Anchor>
    </div>
  </div>
</template>

<style lang="postcss">
.content-card {
  width: 100%;
  background-color: var(--surface-light-00);
  padding: var(--space-60) var(--space-60);
  border: 1px solid var(--ui-neutral-20);
  box-shadow:
    0 4px 8px #00000012,
    0 2px 2px #00000010;
  display: flex;
  flex-direction: column;
  gap: var(--space-60);

  .content-card__content {
    display: flex;
    flex-direction: column;
    gap: var(--space-40);
  }

  .content-card__image {
    aspect-ratio: 16 / 9;
    object-fit: cover;
    height: max-content;
  }

  .content-card__title {
    margin-bottom: 0;

    @media (--tablet-only) {
      max-width: 26rem;
    }
  }

  .content-card__description {
    margin-bottom: var(--space-20);

    @media (--tablet) {
      min-width: 17.5rem;
      max-width: 26rem;
    }

    @media (--desktop) {
      min-width: auto;
    }
  }

  .content-card__link {
    text-transform: uppercase;
    margin-bottom: var(--space-30);
    min-width: fit-content;
    text-decoration: underline;
  }
  &.content-card--unknown {
    @media (--desktop) {
      max-width: 78rem;

      &:has(.content-card__image) {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--space-70);
        justify-content: center;
        align-items: center;

        &:not(:has(.content-card__content)) {
          grid-template-columns: 1fr;
        }

        .content-card__content {
          display: flex;
          flex-direction: column;
          gap: var(--space-40);
        }
      }
      &:not(:has(.content-card__image)) {
        padding: var(--space-80) var(--space-60);

        .content-card__content {
          flex-direction: row;
          justify-content: center;
          align-items: center;
          gap: var(--space-60);
        }

        .content-card__link,
        .content-card__description {
          margin-bottom: 0;
        }
      }
    }
  }
}
</style>
